import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Star, Instagram, ArrowRight, VolumeX, BookOpen, Timer, ChevronDown } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { useCartStore } from '../store/useCartStore';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 24
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: [0.23, 1, 0.32, 1]
}} className={className} data-id="element-105" data-mp="2b2dqvnoy5sro">
    {children}
  </motion.div>;
const FaqItem = ({
  faq,
  delay
}: {
  faq: {
    q: string;
    a: string;
  };
  delay: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <FadeIn delay={delay} data-id="element-106" data-mp="lnz4ha1eihzc">
      <div className="border-b border-brand-primary/10" data-id="element-107" data-mp="2zjgq4wccm2d0">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-7 flex items-center justify-between text-left focus:outline-none group" data-id="element-108" data-mp="2zgu822ru04gj">
          <span className="font-serif font-normal text-brand-primary text-xl lg:text-2xl pr-8 group-hover:opacity-70 transition-opacity duration-200" data-id="element-109" data-mp="38itrt2u8dmn6">
            {faq.q}
          </span>
          <ChevronDown className={`w-4 h-4 text-brand-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} data-id="element-110" data-mp="3i8dbubtojmjm" />
        </button>
        <AnimatePresence data-id="element-111" data-mp="3rbechwzs0lfw">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-112" data-mp="3jsu3ln0xjm">
              <p className="pb-8 text-brand-text text-base lg:text-lg leading-relaxed font-light max-w-2xl" data-id="element-113" data-mp="355ss0l1dllbn">
                {faq.a}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </FadeIn>;
};
export function LandingPage() {
  const navigate = useNavigate();
  const {
    addItem
  } = useCartStore();
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBuyClick = (e: React.MouseEvent, product: (typeof PRODUCTS)[0]) => {
    e.stopPropagation();
    addItem(product);
  };
  const problems = [{
    icon: <VolumeX className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-114" data-mp="3sver78hr00jn" />,
    title: 'Нескінченні нагадування',
    desc: 'Втомилися щодня повторювати «почисть зуби», «прибери іграшки» або «заправ ліжко»?'
  }, {
    icon: <BookOpen className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-115" data-mp="5sidyp6x4kgb" />,
    title: 'Сварки через уроки',
    desc: 'Дитина сідає за домашнє завдання тільки після довгих вмовлянь та зіпсованого настрою?'
  }, {
    icon: <Timer className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-116" data-mp="u9hesy83b69k" />,
    title: 'Стресові ранки',
    desc: 'Ранкові збори в садок або школу перетворюються на хаос і забирають енергію на весь день?'
  }];
  const howItWorks = [{
    title: 'Формує корисні звички',
    desc: 'Вмивання, прибирання, читання стають автоматичними діями.'
  }, {
    title: 'Мотивує через гру та нагороди',
    desc: 'Збери 7 зірочок за тиждень — отримай заслужений приз!'
  }, {
    title: 'Зменшує стрес і нагадування',
    desc: 'Більше жодних нагадувань. Дитина сама бачить свій план.'
  }, {
    title: 'Зона Workout',
    desc: 'Фізична активність щодня: присідання, віджимання, планка.'
  }];
  const faqs = [{
    q: 'Для якого віку підходить планер?',
    a: 'Планер ідеально підходить для дітей від 4 до 10 років. Малюки можуть орієнтуватися по картинках, а старші діти — читати завдання самостійно.'
  }, {
    q: 'Як він кріпиться?',
    a: 'Планер має повністю магнітну основу. Він надійно тримається на холодильнику або будь-якій іншій металевій поверхні і не сповзає.'
  }, {
    q: 'Чим писати на планері?',
    a: 'У комплекті йде спеціальний маркер з губкою. Дитина може легко ставити галочки та стирати їх наприкінці тижня. Поверхня планера багаторазова.'
  }];
  const reviews = [{
    name: 'Анна',
    role: 'мама 6-річного Максима',
    text: 'Це просто порятунок! Раніше кожен ранок починався зі сліз та вмовлянь почистити зуби. Тепер син сам біжить до холодильника відмічати галочки.'
  }, {
    name: 'Олена',
    role: 'мама двійнят',
    text: "Купили відразу два планери. У дітей з'явився змагальний дух! Самі заправляють ліжко, роблять зарядку. Я нарешті можу спокійно випити каву вранці."
  }, {
    name: 'Ігор',
    role: 'тато 8-річної Софії',
    text: "Відмінна річ для привчання до дисципліни без тиску. Донька сама обирає собі 'Завдання тижня' і дуже пишається, коли збирає всі зірочки."
  }];
  return <div className="min-h-screen bg-brand-bg text-brand-text overflow-x-hidden selection:bg-brand-primary selection:text-brand-bg pt-32" data-id="element-117" data-mp="ylx1zkcjjzxw">
      {/* Hero */}
      <section className="relative pt-12 pb-32 lg:pt-24 lg:pb-48 overflow-hidden" data-id="element-118" data-mp="6uh8d6ckhcy6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-119" data-mp="avt0biiwya9j">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-120" data-mp="aw2r38bs8lga">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center lg:text-left order-1" data-id="element-121" data-mp="fu49dr1dwgqp">
              <div className="flex items-center gap-3 justify-center lg:justify-start mb-10" data-id="element-122" data-mp="1w8vrnb4tg7ma">
                <span className="text-[10px] font-medium text-brand-muted tracking-[0.3em] uppercase" data-id="element-123" data-mp="16kqak9714j6e">Преміальна якість</span>
              </div>
              <h1 className="text-6xl md:text-7xl lg:text-[5.5rem] font-serif font-normal leading-[1.1] mb-10 text-brand-primary tracking-tight" data-id="element-124" data-mp="11l3y41z0z6yn">
                Це не просто <br data-id="element-125" data-mp="1bkcpqo6f49hu" />
                <span className="italic text-brand-muted" data-id="element-126" data-mp="1bl4kjws6y372">планер.</span>
              </h1>

              <div className="hidden lg:block mb-16" data-id="element-127" data-mp="ntpepu17h3r2">
                <p className="text-lg text-brand-text leading-relaxed max-w-md font-light" data-id="element-128" data-mp="nse7fk8jxdv7">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="hidden lg:flex flex-col sm:flex-row gap-6 justify-center lg:justify-start" data-id="element-129" data-mp="stbofthts1v4">
                <button onClick={scrollToProducts} className="px-12 py-5 bg-brand-primary hover:bg-brand-primaryHover text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 flex items-center justify-center gap-4 group" data-id="element-130" data-mp="52zbg47q3exo">
                  Обрати дизайн
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-131" data-mp="2gl2tte636u2v" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.9,
            delay: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative order-2 max-w-md mx-auto lg:max-w-full flex flex-col items-center" data-id="element-132" data-mp="26tpy2bh63jrk">
              <div className="relative overflow-hidden bg-brand-surface mb-12 w-full aspect-[4/5] lg:aspect-[3/4]" data-id="element-133" data-mp="2lkfdxr5kpn1r">
                <img src="https://cdn.magicpatterns.com/uploads/cdrFBjpV29uCPC4QsJUG8h/IMG_5206.png" alt="MyPlaner - Інтерактивний планер для дітей" className="w-full h-full object-cover" data-id="element-134" data-mp="1a35wfa609533" />
              </div>

              <div className="lg:hidden mb-12 text-center w-full" data-id="element-135" data-mp="25ppuhwvu7ghf">
                <p className="text-base text-brand-text leading-relaxed font-light" data-id="element-136" data-mp="20rfy5iydm9u8">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="flex lg:hidden flex-col sm:flex-row gap-4 w-full justify-center" data-id="element-137" data-mp="2oikb0v93zgnr">
                <button onClick={scrollToProducts} className="w-full px-8 py-5 bg-brand-primary hover:bg-brand-primaryHover text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 flex items-center justify-center gap-4" data-id="element-138" data-mp="2kgyr4py5q242">
                  Обрати дизайн
                  <ArrowRight className="w-4 h-4" data-id="element-139" data-mp="387md2f4wqs9m" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="py-32 lg:py-48 bg-brand-surface" data-id="element-140" data-mp="3aris72k7qhm2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-141" data-mp="3atbxoej3avsh">
          <FadeIn data-id="element-142" data-mp="3fr551aw4pvuc">
            <div className="text-center mb-24 lg:mb-32" data-id="element-143" data-mp="3fqc44qjuq0k4">
              <h2 className="text-5xl md:text-6xl font-serif font-normal text-brand-primary mb-8 tracking-tight" data-id="element-144" data-mp="3konb8tirhwyn">
                Знайомі ситуації?
              </h2>
              <p className="text-xl text-brand-muted max-w-2xl mx-auto font-light" data-id="element-145" data-mp="3oofgaw8y5kz9">
                Кожен день схожий на день бабака, а прості справи перетворюються
                на випробування.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-16 lg:gap-24" data-id="element-146" data-mp="7blcyf772jgv">
            {problems.map((item, idx) => <FadeIn key={idx} delay={idx * 0.15} data-id="element-147" data-mp="c8txunjq91lt" data-mpi={idx}>
                <div className="h-full flex flex-col items-center text-center" data-id="element-148" data-mp="gb8gp7nec1d3">
                  <div className="mb-8" data-id="element-149" data-mp="g94503j99y29">{item.icon}</div>
                  <h3 className="text-2xl font-serif font-normal mb-4 text-brand-primary" data-id="element-150" data-mp="latg1j6habqc">
                    {item.title}
                  </h3>
                  <p className="text-brand-muted text-base leading-relaxed font-light" data-id="element-151" data-mp="1l6b57tkst8kf">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* How it works + Buy */}
      <section className="py-32 lg:py-48 bg-brand-bg" data-id="element-152" data-mp="1uy7em0sc4l3y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-153" data-mp="1yyhmxk3lnnlz">
          <div className="grid lg:grid-cols-2 gap-20 lg:gap-32 items-center" data-id="element-154" data-mp="23wsrpgf1f7xc">
            <div className="relative overflow-hidden bg-brand-surface aspect-[4/5] lg:aspect-[3/4] order-2 lg:order-1" data-id="element-155" data-mp="23yvrut9wmhfs">
              <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-156" data-mp="2duyrnshaa3zk" />
            </div>

            <div className="flex flex-col order-1 lg:order-2" data-id="element-157" data-mp="jisj71fry1ay">
              <FadeIn data-id="element-158" data-mp="2ws0opxiqxjq3">
                <h2 className="text-5xl md:text-6xl font-serif font-normal text-brand-primary mb-16 tracking-tight" data-id="element-159" data-mp="2wtbt9zk0mvi9">
                  Як працює MyPlaner?
                </h2>

                <div className="mb-16 space-y-12" data-id="element-160" data-mp="2nzoxi9ft5s92">
                  {howItWorks.map((item, idx) => <div key={idx} className="flex gap-8" data-id="element-161" data-mp="2j1dscc0gk5x6" data-mpi={idx}>
                      <span className="text-xs font-medium text-brand-muted tabular-nums pt-1 w-6 flex-shrink-0 tracking-widest" data-id="element-162" data-mp="3fnqtfsi2cu7o">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div data-id="element-163" data-mp="31qaj45wncgan">
                        <h4 className="text-xl font-serif font-normal text-brand-primary mb-2" data-id="element-164" data-mp="31r3mqy0b6pok">
                          {item.title}
                        </h4>
                        <p className="text-brand-muted text-base leading-relaxed font-light" data-id="element-165" data-mp="1qb56oa6pr3du">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-10 pt-8 border-t border-brand-primary/10" data-id="element-166" data-mp="1wq3wywmmok3n">
                  <div className="text-center sm:text-left" data-id="element-167" data-mp="1nqpdih4vp9sn">
                    <p className="text-[10px] text-brand-muted font-medium uppercase tracking-[0.3em] mb-4" data-id="element-168" data-mp="1now26ojbm1eb">
                      Інвестиція в спокій
                    </p>
                    <div className="text-5xl font-serif font-normal text-brand-primary" data-id="element-169" data-mp="1dtkv8se5lyh4">
                      1000 <span className="text-xl font-sans font-light text-brand-muted" data-id="element-170" data-mp="102639w29qmxx">грн</span>
                    </div>
                  </div>
                  <button onClick={scrollToProducts} className="w-full sm:w-auto px-12 py-5 bg-brand-primary hover:bg-brand-primaryHover text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 flex items-center justify-center gap-4 group" data-id="element-171" data-mp="140n7o7ub1jbn">
                    Замовити зараз
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-172" data-mp="6igkmf3w5n4o" />
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-32 lg:py-48 bg-brand-surface" data-id="element-173" data-mp="uem0cv00pt12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-174" data-mp="pg11aywi5fe7">
          <FadeIn data-id="element-175" data-mp="pfhuad8e4bey">
            <div className="text-center mb-24 lg:mb-32" data-id="element-176" data-mp="pdydmlitnycn">
              <h2 className="text-5xl md:text-6xl font-serif font-normal text-brand-primary mb-8 tracking-tight" data-id="element-177" data-mp="143vuegef9lrt">
                Оберіть свій дизайн
              </h2>
              <p className="text-xl text-brand-muted max-w-2xl mx-auto font-light" data-id="element-178" data-mp="1d5c5yrgz3txm">
                Створені з увагою до деталей, щоб ідеально доповнити інтер'єр дитячої кімнати.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16" data-id="element-179" data-mp="32lpm32wn4p6c">
            {PRODUCTS.map((product, idx) => <FadeIn key={product.id} delay={idx * 0.15} data-id="element-180" data-mp="37jzi1fq6vtt0" data-mpi={idx}>
                <div onClick={() => navigate(`/product/${product.id}`)} className="group flex flex-col h-full cursor-pointer" data-id="element-181" data-mp="3ccah6gd3vbki">
                  <div className="relative aspect-[3/4] overflow-hidden bg-brand-surfaceAlt mb-8" data-id="element-182" data-mp="2jo4uvp6eleik">
                    <img src={product.img} alt={product.title} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-1000 ease-out" data-id="element-183" data-mp="2ompyzzos0ica" />
                  </div>
                  <div className="flex flex-col flex-grow text-center" data-id="element-184" data-mp="8vtxnf3d0njr">
                    <h3 className="text-2xl font-serif font-normal text-brand-primary mb-3" data-id="element-185" data-mp="8vk6vpahqcd0">
                      {product.title}
                    </h3>
                    <p className="text-brand-muted text-sm mb-8 flex-grow line-clamp-2 font-light" data-id="element-186" data-mp="1xfpn2a6vvtmo">
                      {product.desc}
                    </p>

                    <div className="flex flex-col items-center gap-6 mt-auto" data-id="element-187" data-mp="1tg8idiza70ik">
                      <div className="text-2xl font-serif font-normal text-brand-primary" data-id="element-188" data-mp="1olbd8n54hbay">
                        {product.price} <span className="text-sm font-sans font-light text-brand-muted" data-id="element-189" data-mp="1jlyo1vqs3s57">грн</span>
                      </div>
                      <button onClick={e => handleBuyClick(e, product)} className="text-brand-primary hover:text-brand-muted text-[10px] uppercase tracking-[0.3em] font-medium transition-colors duration-300 border-b border-brand-primary pb-1" data-id="element-190" data-mp="19q53bp2woscc">
                        Купити
                      </button>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32 lg:py-48 bg-brand-bg" data-id="element-191" data-mp="3e71o9fxg3hjf">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-192" data-mp="3iybas7np64r3">
          <FadeIn data-id="element-193" data-mp="3izurfzd9mhte">
            <div className="text-center mb-24" data-id="element-194" data-mp="3j0eaxkikde9j">
              <h2 className="text-5xl md:text-6xl font-serif font-normal text-brand-primary mb-8 tracking-tight" data-id="element-195" data-mp="3587rnpk46r5v">
                Часті запитання
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-4" data-id="element-196" data-mp="1rhjp54z241j">
            {faqs.map((faq, idx) => <FaqItem key={idx} faq={faq} delay={idx * 0.1} data-id="element-197" data-mp="5sjixlyw2g3i" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 lg:py-48 bg-brand-surface" data-id="element-198" data-mp="3nxehrxn8ml1p">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-199" data-mp="3svxztmypncos">
          <FadeIn data-id="element-200" data-mp="3sykavwkt8abr">
            <div className="flex items-center gap-3 justify-center mb-12" data-id="element-201" data-mp="2cir71e2qkek7">
              <span className="text-[10px] font-medium text-brand-muted tracking-[0.3em] uppercase" data-id="element-202" data-mp="2cku24ciqwxvs">
                Зроблено в Україні
              </span>
            </div>

            <div className="text-center mb-24 lg:mb-32" data-id="element-203" data-mp="29w89fvz04par">
              <h2 className="text-5xl md:text-6xl font-serif font-normal text-brand-primary mb-8 tracking-tight" data-id="element-204" data-mp="24zrlp2vpscy7">
                Батьки рекомендують
              </h2>
              <p className="text-xl text-brand-muted font-light" data-id="element-205" data-mp="2dzn2bn81h05m">
                Сотні сімей вже забули про крики вранці.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-16 lg:gap-24" data-id="element-206" data-mp="1qac0e2eyczpw">
            {reviews.map((review, idx) => <FadeIn key={idx} delay={idx * 0.15} className="py-10 md:py-0" data-id="element-207" data-mp="1m6noj7jafhyu" data-mpi={idx}>
                <div className="h-full flex flex-col items-center text-center" data-id="element-208" data-mp="2016kp18n1p8p">
                  <div className="flex gap-1.5 mb-10" data-id="element-209" data-mp="1v8dfydd66gwm">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-brand-primary fill-brand-primary" data-id="element-210" data-mp="17hzw3a4qnod4" data-mpi={i} />)}
                  </div>
                  <p className="text-brand-text text-lg leading-relaxed mb-10 flex-grow font-light italic" data-id="element-211" data-mp="i0h50z6aqzk0">
                    "{review.text}"
                  </p>
                  <p className="font-serif font-normal text-brand-primary text-xl" data-id="element-212" data-mp="qyb0k7w8lp64">
                    {review.name}
                  </p>
                  <p className="text-brand-muted font-sans font-light text-sm mt-2" data-id="element-213" data-mp="vwcigphxfig0">
                    {review.role}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-primary text-brand-bg pt-32 pb-16" data-id="element-214" data-mp="19qvg6nmxdxrz">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-215" data-mp="1eonfi2pwgm67">
          <FadeIn data-id="element-216" data-mp="1eqrjslrpnn70">
            <h2 className="text-5xl md:text-6xl font-serif font-normal mb-10 tracking-tight" data-id="element-217" data-mp="1jo9v2it7aei6">
              Готові до спокійних ранків?
            </h2>
            <p className="text-xl text-brand-bg/60 mb-16 max-w-xl mx-auto font-light" data-id="element-218" data-mp="1onmf6vsot7h2">
              Замовте MyPlaner сьогодні та почніть формувати корисні звички
              вашої дитини вже завтра.
            </p>

            <button className="px-12 py-5 bg-white hover:bg-brand-surface text-brand-primary text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 mb-32 inline-flex items-center gap-4" data-id="element-219" data-mp="3485aq6kklxkd">
              Замовити за 1000 грн
              <ArrowRight className="w-4 h-4" data-id="element-220" data-mp="3rvwjj06wjsgw" />
            </button>
          </FadeIn>

          <div className="border-t border-white/10 pt-16 flex flex-col md:flex-row justify-between items-center gap-10" data-id="element-221" data-mp="3mw0fj0y7g11c">
            <div className="text-3xl font-serif font-normal tracking-tight" data-id="element-222" data-mp="3i4s644wvkfo6">
              MyPlaner<span className="text-brand-accent" data-id="element-223" data-mp="3d7ik6z8p2m53">.</span>
            </div>

            <div className="flex items-center gap-8" data-id="element-224" data-mp="egqi4659fjzw">
              <a href="https://www.instagram.com/myplaner.ua/p/DXq4XE3jJA8/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-brand-bg/60 hover:text-brand-bg transition-colors duration-300 text-[10px] uppercase tracking-[0.3em] font-medium" data-id="element-225" data-mp="eibd1wvhl4ya">
                <Instagram className="w-4 h-4" strokeWidth={1.5} data-id="element-226" data-mp="1r9wh5ihjh03h" />
                <span data-id="element-227" data-mp="1rca8phd0fu6a">@myplaner.ua</span>
              </a>
            </div>
          </div>

          <div className="mt-16 text-brand-bg/40 text-xs flex flex-col md:flex-row justify-center gap-10 font-light" data-id="element-228" data-mp="1mcxnt25p8x6c">
            <a href="#" className="hover:text-brand-bg transition-colors duration-300" data-id="element-229" data-mp="2j36ninims8w7">
              Умови доставки та оплати
            </a>
            <a href="#" className="hover:text-brand-bg transition-colors duration-300" data-id="element-230" data-mp="2a80ap4ky0qk6">
              Політика конфіденційності
            </a>
            <span data-id="element-231" data-mp="257unc0lphq7w">
              © {new Date().getFullYear()} MyPlaner. Всі права захищені.
            </span>
          </div>
        </div>
      </footer>
    </div>;
}